"use client";

/**
 * PreviousTradesTable — ALL trades (active + closed) in one table.
 *
 * Active rows come from the signal's open positions and are tagged ACTIVE;
 * closed rows show the exit reason (hit target / stopped / time exit).
 * Each row expands to a chart over the fired→closed window with entry/exit
 * marked, as proof the levels actually hit.
 *
 * Styling contract: white text only, emerald accent (no blue), minimal
 * rounding, tight padding.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Check, X, Clock, Activity, ArrowUp, ArrowDown, ChevronsUpDown, Loader2, AlertTriangle } from "lucide-react";
import type { PreviousTrade, TradePosition } from "@/lib/mock/signal-performance";
import { SignalTradeChart } from "./SignalTradeChart";
import { OptionPayoffChart } from "./OptionPayoffChart";
import { PlotlyChart } from "@/components/notebook/PlotlyChart";
import { apiClient, type OptionsPnlSnapshot, type OptionsPnlAnalysis } from "@/lib/api-client";

type MaybeNumber = number | null | undefined;

function isFiniteNumber(n: MaybeNumber): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

function fmt(n: MaybeNumber) {
  return isFiniteNumber(n) ? n.toFixed(2) : "—";
}

function fmtMoney(n: MaybeNumber) {
  return isFiniteNumber(n) ? `$${fmt(n)}` : "—";
}

function fmtPct(n: MaybeNumber) {
  if (!isFiniteNumber(n)) return "—";
  return `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;
}

function fmtDate(iso?: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** One unified row shape for both active positions and closed trades. */
interface TradeRow {
  key: string;
  kind: PreviousTrade["kind"];
  ticker: string;
  underlying_ticker?: string;
  name?: string;
  strategy_type?: string;
  direction: PreviousTrade["direction"];
  entry: MaybeNumber;
  /** Exit price for closed trades; live mark for active ones. */
  mark: MaybeNumber;
  exit_rule?: PreviousTrade["exit_rule"];
  exit_reason: PreviousTrade["exit_reason"] | "active";
  pnl_pct: MaybeNumber;
  pnl_usd?: number;
  notional_usd?: number;
  fired_at: string;
  closed_at?: string;
  legs?: PreviousTrade["legs"];
  criteria?: Record<string, string>;
  seeded_from_backtest?: boolean;
  snapshot?: OptionsPnlSnapshot;
  snapshot_loading?: boolean;
  illiquid?: boolean;
  worst_leg_oi?: number;
}

function toRowFromClosed(t: PreviousTrade, snapshot?: OptionsPnlSnapshot, snapshotLoading = false): TradeRow {
  const entry = typeof snapshot?.entry === "number" ? snapshot.entry : t.entry;
  const mark = typeof snapshot?.exit === "number" ? snapshot.exit : t.exit_price;
  const pnlPct = typeof snapshot?.pnl_pct === "number" ? snapshot.pnl_pct : t.pnl_pct;
  const pnlUsd = typeof snapshot?.pnl_usd === "number" ? snapshot.pnl_usd : t.pnl_usd;
  const legs = snapshot?.legs?.length
    ? t.legs?.map((leg, i) => ({
        ...leg,
        entry: typeof snapshot.legs?.[i]?.entry === "number" ? snapshot.legs[i].entry! : leg.entry,
        exit_price: typeof snapshot.legs?.[i]?.exit === "number" ? snapshot.legs[i].exit! : leg.exit_price,
        bid: typeof snapshot.legs?.[i]?.exit_bid === "number" ? snapshot.legs[i].exit_bid! : leg.bid,
        ask: typeof snapshot.legs?.[i]?.exit_ask === "number" ? snapshot.legs[i].exit_ask! : leg.ask,
      }))
    : t.legs;
  return {
    key: `closed-${t.ticker}-${t.fired_at}-${t.closed_at}`,
    kind: t.kind,
    ticker: t.ticker,
    underlying_ticker: t.underlying_ticker,
    name: t.name,
    strategy_type: t.strategy_type,
    direction: t.direction,
    entry,
    mark,
    exit_rule: t.exit_rule,
    exit_reason: t.exit_reason,
    pnl_pct: pnlPct,
    pnl_usd: pnlUsd,
    notional_usd: t.notional_usd,
    fired_at: t.fired_at,
    closed_at: t.closed_at,
    legs,
    criteria: t.criteria,
    seeded_from_backtest: t.seeded_from_backtest,
    snapshot,
    snapshot_loading: snapshotLoading,
  };
}

function toRowFromActive(p: TradePosition): TradeRow {
  // Live PnL is always derived from the displayed entry/current pair so the
  // row can never contradict its own numbers.
  const entry = isFiniteNumber(p.entry) ? p.entry : 0;
  const current = isFiniteNumber(p.current_price) ? p.current_price : entry;
  const pnl =
    entry > 0
      ? ((p.direction === "short" ? entry - current : current - entry) /
          entry) *
        100
      : 0;
  return {
    key: `active-${p.id || p.ticker}-${p.opened_at ?? ""}`,
    kind: p.kind,
    ticker: p.ticker,
    underlying_ticker: p.underlying_ticker,
    name: p.name,
    strategy_type: p.strategy_type,
    direction: p.direction,
    entry,
    mark: current,
    exit_rule: p.exit,
    exit_reason: "active",
    pnl_pct: pnl,
    fired_at: p.opened_at ?? "",
    legs: p.legs as PreviousTrade["legs"],
    criteria: p.criteria,
    illiquid: p.illiquid,
    worst_leg_oi: p.worst_leg_oi,
  };
}

function legLabel(t: TradeRow): string {
  if (t.name) return t.name;
  return t.ticker;
}

function exitRuleLabel(t: TradeRow): string {
  const r = t.exit_rule;
  if (!r) return "—";
  const tp = r.take_profit != null ? `$${fmt(r.take_profit)}` : null;
  const sl =
    r.stop_loss != null
      ? `$${fmt(r.stop_loss)}`
      : r.trail_pct != null
        ? `${Math.round(r.trail_pct * 100)}% trail`
        : null;
  if (tp && sl) return `${tp} / ${sl}`;
  return tp ?? sl ?? "—";
}

function StatusBadge({ reason }: { reason: TradeRow["exit_reason"] }) {
  const map = {
    active: {
      label: "Active",
      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
      Icon: Activity,
    },
    hit_target: {
      label: "Hit target",
      cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
      Icon: Check,
    },
    stopped: { label: "Stopped", cls: "bg-red-500/20 text-red-300 border-red-500/30", Icon: X },
    time_exit: { label: "Time exit", cls: "bg-white/10 text-white border-white/20", Icon: Clock },
  }[reason];
  const { Icon } = map;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10.5px] font-bold tracking-wider border whitespace-nowrap ${map.cls}`}
    >
      <Icon className="w-3 h-3" />
      {map.label}
    </span>
  );
}

function Row({
  trade,
  criteriaKeys,
  viewportW,
  isMock = false,
}: {
  trade: TradeRow;
  criteriaKeys: string[];
  viewportW: number;
  isMock?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hasPnl = isFiniteNumber(trade.pnl_pct);
  const up = (trade.pnl_pct ?? 0) >= 0;
  const isActive = trade.exit_reason === "active";
  const entryTs = trade.fired_at ? Math.floor(new Date(trade.fired_at).getTime() / 1000) : 0;
  const exitTs = trade.closed_at
    ? Math.floor(new Date(trade.closed_at).getTime() / 1000)
    : undefined;
  const isOption = trade.kind === "option";
  const legs = trade.legs;
  const chartTicker = trade.underlying_ticker || trade.ticker;
  const referenceLines = isOption
    ? trade.legs
        ?.filter((leg) => leg.strike != null)
        .map((leg) => ({
          price: leg.strike as number,
          title: `${leg.action === "sell" ? "Sell" : "Buy"} ${leg.strike}${leg.right === "call" ? "C" : "P"}`,
          color: leg.action === "sell" ? "#f97316" : "#34d399",
        }))
    : undefined;
  const pricedOptionLegs = useMemo(
    () =>
      isOption && legs
        ? legs.filter(
            (leg): leg is NonNullable<typeof legs>[number] & { entry: number; strike: number } =>
              isFiniteNumber(leg.entry) && isFiniteNumber(leg.strike),
          )
        : [],
    [isOption, legs],
  );
  const hasCompleteLegPricing = isOption && !!legs?.length && pricedOptionLegs.length === legs.length;
  const chartEntry = isFiniteNumber(trade.entry) ? trade.entry : undefined;
  const chartMark = isFiniteNumber(trade.mark) ? trade.mark : undefined;
  const trailingStopBase = isFiniteNumber(isActive ? trade.mark : trade.entry)
    ? (isActive ? trade.mark : trade.entry)
    : undefined;

  // Rich optionlab P&L analysis (Plotly diagram + heatmap), lazily fetched the
  // first time an option row is expanded. Mirrors CurrentTradeCard so closed
  // option trades get the same rich view, not just the underlying chart.
  const [pnl, setPnl] = useState<OptionsPnlAnalysis | null>(null);
  const [pnlLoading, setPnlLoading] = useState(false);
  const [pnlError, setPnlError] = useState<string | null>(null);
  const legsKey = useMemo(
    () =>
      isOption && legs
        ? legs
            .map((l) => `${l.right}:${l.strike}:${l.action}:${l.quantity ?? 1}:${l.expiry}`)
            .join("|")
        : "",
    [isOption, legs],
  );

  useEffect(() => {
    if (!open || !isOption || isMock || !legsKey || !hasCompleteLegPricing) return;
    if (pnl || pnlLoading) return;
    let cancelled = false;
    setPnlLoading(true);
    setPnlError(null);
    apiClient
      .getOptionsPnlAnalysis({
        underlying: trade.underlying_ticker || trade.ticker,
        title: legLabel(trade),
        // CLOSED trades have already-expired contracts; tell the engine to treat
        // the entry date as "today" so it renders the entry→expiry payoff instead
        // of refusing it as expired. Live positions use the real today (no as_of).
        ...(isActive ? {} : { as_of: trade.fired_at }),
        legs: pricedOptionLegs.map((l) => ({
          right: l.right,
          strike: l.strike,
          // The payoff curve is anchored to the position's ENTRY cost basis (what
          // we actually opened at), for BOTH active and closed trades. The live /
          // exit state is shown separately (Current spot marker, pnl %, exit
          // price), so the curve must never be rebuilt off the mark/exit price.
          // Feeding exit_price here produced nonsensical metrics (e.g. CRM showing
          // ~$16 credit instead of the real ~$338).
          current_price: l.entry,
          action: l.action,
          quantity: l.quantity ?? 1,
          expiry: l.expiry,
        })),
      })
      .then((res) => {
        if (!cancelled) setPnl(res);
      })
      .catch((e) => {
        if (!cancelled) setPnlError(e instanceof Error ? e.message : "P&L analysis unavailable");
      })
      .finally(() => {
        if (!cancelled) setPnlLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isOption, isMock, legsKey, hasCompleteLegPricing, pricedOptionLegs]);

  return (
    <>
      <tr
        className="cursor-pointer hover:bg-emerald-500/[0.06] transition-colors border-t border-white/10"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            {open ? (
              <ChevronDown className="w-4 h-4 text-emerald-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-white/60" />
            )}
            <div className="flex flex-col">
              <span className="font-bold text-white whitespace-nowrap inline-flex items-center gap-1.5">
                {legLabel(trade)}
                {trade.illiquid && (
                  <span
                    title={`Thin liquidity${
                      trade.worst_leg_oi != null ? ` (worst leg OI ${trade.worst_leg_oi})` : ""
                    } — hard to fill at the quoted price`}
                    className="inline-flex items-center gap-1 rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-300"
                  >
                    <AlertTriangle className="h-3 w-3" />
                    Illiquid
                  </span>
                )}
              </span>
              {trade.strategy_type && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-white/70">
                  {trade.strategy_type}
                </span>
              )}
            </div>
          </div>
        </td>
        <td className="px-3 py-2">
          <StatusBadge reason={trade.exit_reason} />
        </td>
        {/* PnL up front — visible without horizontal scrolling */}
        <td className="px-3 py-2 text-right">
          <span
            className={`tabular-nums text-sm font-bold ${hasPnl ? (up ? "text-emerald-400" : "text-red-400") : "text-white/80"}`}
          >
            {fmtPct(trade.pnl_pct)}
          </span>
          {trade.snapshot && (
            <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-300/75">
              quote snapshot
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-white font-medium whitespace-nowrap">
          {fmtDate(trade.fired_at)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-white font-medium">
          {fmtMoney(trade.entry)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-white font-medium">
          {fmtMoney(trade.mark)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-white font-medium whitespace-nowrap">
          {exitRuleLabel(trade)}
        </td>
        {criteriaKeys.map((k) => (
          <td
            key={k}
            className="px-3 py-2 text-right tabular-nums text-white font-medium whitespace-nowrap"
          >
            {trade.criteria?.[k] ?? "—"}
          </td>
        ))}
      </tr>
      {open && (
        <tr>
          <td colSpan={7 + criteriaKeys.length} className="px-3 pb-4 pt-1 bg-black/60">
            <div
              className="sticky left-0"
              style={{ width: viewportW ? viewportW - 24 : "100%", maxWidth: "100%" }}
            >
            {/* Option rows lead with the rich optionlab P&L analysis + the
                actual contract chart(s); the underlying is demoted to context.
                Equity rows keep the underlying chart with trade levels. */}
            {isOption && legs ? (
              <>
                {pnl?.strategy_metrics && (
                  <div className="mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {[
                      {
                        label: "Net cost",
                        value: `$${Math.abs(pnl.strategy_metrics.net_cost).toLocaleString(undefined, { maximumFractionDigits: 0 })}${pnl.strategy_metrics.net_cost < 0 ? " cr" : ""}`,
                      },
                      {
                        label: "Max profit",
                        value: `$${pnl.strategy_metrics.max_profit.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                      },
                      {
                        label: "Max loss",
                        value: `$${pnl.strategy_metrics.max_loss.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                      },
                      {
                        label: "Prob. of profit",
                        value: `${Math.round(pnl.strategy_metrics.probability_of_profit * 100)}%`,
                      },
                    ].map((m) => (
                      <div key={m.label} className="rounded-sm border border-white/10 bg-black/40 px-2.5 py-1.5">
                        <div className="text-[9px] font-bold uppercase tracking-widest text-white/45">{m.label}</div>
                        <div className="mt-0.5 text-sm font-bold tabular-nums text-white">{m.value}</div>
                      </div>
                    ))}
                  </div>
                )}

                {pnlLoading && !pnl && (
                  <div className="rounded-sm border border-white/10 bg-black/40 px-4 py-5">
                    <div className="flex items-center gap-2 text-[12px] font-semibold text-white/70">
                      <Loader2 className="w-4 h-4 animate-spin text-emerald-300" />
                      Computing P&amp;L diagram and heatmap for {legLabel(trade)}…
                    </div>
                    <div className="mt-4 h-[320px] animate-pulse rounded-sm bg-white/[0.04]" />
                    <div className="mt-3 h-[280px] animate-pulse rounded-sm bg-white/[0.035]" />
                  </div>
                )}

                {pnl?.pnl_diagram_figure && (
                  <div key={`${legsKey}-pnl`} className="mb-3 rounded-sm border border-white/10 bg-black/40 overflow-hidden">
                    <PlotlyChart data={pnl.pnl_diagram_figure} noBorder />
                  </div>
                )}
                {pnl?.heatmap_figure && (
                  <div key={`${legsKey}-heat`} className="mb-3 rounded-sm border border-white/10 bg-black/40 overflow-hidden">
                    <PlotlyChart data={pnl.heatmap_figure} noBorder />
                  </div>
                )}

                {/* Fallback payoff tent: mock mode, backend error, or while the
                    rich analysis hasn't returned yet. */}
                {!pnl?.pnl_diagram_figure && !pnlLoading && hasCompleteLegPricing && (
                  <div className="mb-3">
                    {pnlError && (
                      <p className="mb-2 text-[11px] text-white/80">
                        Showing the quick payoff view. Full P&amp;L analysis is unavailable right now.
                      </p>
                    )}
                    <OptionPayoffChart
                      legs={pricedOptionLegs}
                      title={`${legLabel(trade)} payoff at expiry (per contract)`}
                    />
                  </div>
                )}

                {/* The actual option contract chart(s), one per leg. */}
                {!hasCompleteLegPricing && (
                  <div className="mb-3 rounded-sm border border-white/10 bg-black/40 px-3 py-2 text-[12px] font-semibold text-white/80">
                    Per-leg price history is unavailable for this seeded backtest trade. The net entry,
                    exit, and P&amp;L in the row above are still valid.
                  </div>
                )}
                {hasCompleteLegPricing && pricedOptionLegs.map((leg, i) => {
                  const occ = leg.polygon_ticker ?? leg.ticker;
                  if (!occ) return null;
                  return (
                    <div className="mb-3" key={`prev-contract-${occ}-${i}`}>
                      <SignalTradeChart
                        ticker={occ}
                        label={`${leg.right ? `${leg.ticker} ${leg.strike}${leg.right === "call" ? "C" : "P"}` : leg.ticker} contract`}
                        direction={leg.action === "sell" ? "short" : "long"}
                        entryTs={entryTs}
                        exitTs={exitTs}
                        entry={leg.entry}
                        exitPrice={isActive ? undefined : leg.exit_price ?? undefined}
                        underlyingTicker={leg.underlying_ticker ?? trade.underlying_ticker ?? trade.ticker}
                        toSec={isActive ? Math.floor(Date.now() / 1000) : undefined}
                        tradeRangeSelector
                        showTradeLevels={false}
                        referenceLines={[{ price: leg.entry, title: "Entry", color: "#ffffff" }]}
                        timespan="hour"
                        seriesType="line"
                        height={280}
                      />
                    </div>
                  );
                })}

                {/* Underlying context. */}
                <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-white/80">
                  Underlying: {chartTicker} (context)
                </div>
                <SignalTradeChart
                  ticker={chartTicker}
                  label={legLabel(trade)}
                  direction={trade.direction}
                  entryTs={entryTs}
                  exitTs={exitTs}
                  autoFit={false}
                  toSec={isActive ? Math.floor(Date.now() / 1000) : undefined}
                  tradeRangeSelector
                  showTradeLevels={false}
                  referenceLines={referenceLines}
                  height={280}
                />
              </>
            ) : (
              <SignalTradeChart
                ticker={chartTicker}
                label={legLabel(trade)}
                direction={trade.direction}
                entryTs={entryTs}
                exitTs={exitTs}
                // Real data everywhere: draw the trade's actual entry / target /
                // stop so the chart matches the TP/SL column, for BOTH active and
                // closed rows. autoFit was a mock-only crutch (synthetic levels);
                // the polygon route returns real candles, so real levels align.
                // Closed rows also get an Exit marker at their real exit price.
                autoFit={false}
                entry={chartEntry}
                target={trade.exit_rule?.take_profit}
                stop={
                  trade.exit_rule?.stop_loss ??
                  (trade.exit_rule?.trail_pct != null && trailingStopBase != null
                    ? trailingStopBase *
                      (1 - trade.exit_rule.trail_pct)
                    : undefined)
                }
                exitPrice={isActive ? undefined : chartMark}
                toSec={isActive ? Math.floor(Date.now() / 1000) : undefined}
                tradeRangeSelector
                showTradeLevels
                height={300}
              />
            )}
            {isOption && trade.snapshot && (
              <div className="mt-3 rounded-sm border border-emerald-500/25 bg-emerald-500/[0.06] px-3 py-2 text-[11px] text-white/70">
                Recomputed from historical option marks at entry and exit. Entry {fmtMoney(trade.entry)} to exit {fmtMoney(trade.mark)}
                {trade.pnl_usd != null ? ` (${trade.pnl_usd >= 0 ? "+" : ""}$${Math.abs(trade.pnl_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })})` : ""}.
              </div>
            )}
            {isOption && trade.legs && (
              <div className="mt-3 overflow-x-auto rounded-sm border border-white/10">
                <table className="w-full min-w-[560px] text-xs">
                  <thead>
                    <tr className="bg-black/70 text-[10px] font-bold uppercase tracking-wider text-white/80">
                      <th className="px-3 py-2 text-left">Leg</th>
                      <th className="px-3 py-2 text-left">Action</th>
                      <th className="px-3 py-2 text-right">Entry</th>
                      <th className="px-3 py-2 text-right">{isActive ? "Current" : "Exit"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trade.legs.map((leg, i) => (
                      <tr
                        key={`${leg.polygon_ticker ?? leg.ticker}-${i}`}
                        className={i % 2 ? "bg-white/[0.02]" : ""}
                      >
                        <td className="px-3 py-2 font-bold text-white">
                          {leg.right
                            ? `${leg.ticker} ${leg.strike}${leg.right === "call" ? "C" : "P"}`
                            : leg.ticker}
                        </td>
                        <td className="px-3 py-2 text-white capitalize">
                          {leg.action ?? "buy"} {leg.quantity ?? 1}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-white">
                          {fmtMoney(leg.entry)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-300">
                          {leg.exit_price != null
                            ? fmtMoney(leg.exit_price)
                            : leg.current_price != null
                              ? fmtMoney(leg.current_price)
                              : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

type SortDir = "asc" | "desc";

/** Sortable value for a column key. Numeric where it makes sense. */
function sortValue(t: TradeRow, key: string): string | number {
  switch (key) {
    case "trade":
      return (t.name || t.ticker).toLowerCase();
    case "status":
      return t.exit_reason === "active" ? "0" : `1-${t.exit_reason}`;
    case "pnl":
      return isFiniteNumber(t.pnl_pct) ? t.pnl_pct : Number.NEGATIVE_INFINITY;
    case "fired":
      return t.fired_at || "";
    case "entry":
      return isFiniteNumber(t.entry) ? t.entry : Number.NEGATIVE_INFINITY;
    case "mark":
      return isFiniteNumber(t.mark) ? t.mark : Number.NEGATIVE_INFINITY;
    default: {
      const raw = t.criteria?.[key] ?? "";
      const num = parseFloat(raw.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(num) && raw !== "" ? num : raw.toLowerCase();
    }
  }
}

function SortableTh({
  label,
  colKey,
  align = "right",
  sort,
  onSort,
}: {
  label: string;
  colKey: string;
  align?: "left" | "right";
  sort: { key: string; dir: SortDir } | null;
  onSort: (key: string) => void;
}) {
  const active = sort?.key === colKey;
  // Active column shows the real direction; idle columns show a faint
  // double-chevron so the user knows every column is clickable/sortable.
  return (
    <th
      onClick={() => onSort(colKey)}
      className={`px-3 py-2 whitespace-nowrap cursor-pointer select-none transition-colors ${
        align === "left" ? "text-left" : "text-right"
      } ${active ? "text-emerald-300" : "text-white/80 hover:text-white"}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {label}
        {active ? (
          sort?.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        ) : (
          <ChevronsUpDown className="w-3 h-3 text-white/35" />
        )}
      </span>
    </th>
  );
}

export function PreviousTradesTable({
  trades,
  activePositions = [],
  isMock = false,
}: {
  trades: PreviousTrade[];
  activePositions?: TradePosition[];
  isMock?: boolean;
}) {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null);
  const [snapshots, setSnapshots] = useState<Record<string, OptionsPnlSnapshot | "loading" | "error">>({});
  // The table can be wider than the screen (many criteria columns), so it
  // scrolls horizontally. The expanded chart lives in a full-width <td> that
  // would push its right price axis off-screen. We measure the *visible*
  // scroll viewport and pin the chart to it (sticky) so the axis is always shown.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setViewportW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const onSort = (key: string) =>
    setSort((prev) =>
      prev?.key === key ? { key, dir: prev.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" },
    );

  const snapshotKey = (t: PreviousTrade) => `${t.id || t.ticker}-${t.fired_at}-${t.closed_at}`;

  useEffect(() => {
    const candidates = trades.filter(
      (t) =>
        t.kind === "option" &&
        !t.seeded_from_backtest &&
        t.legs?.length &&
        t.fired_at &&
        t.closed_at &&
        snapshots[snapshotKey(t)] == null,
    );
    if (!candidates.length) return;

    let cancelled = false;
    candidates.slice(0, 8).forEach((t) => {
      const key = snapshotKey(t);
      setSnapshots((prev) => ({ ...prev, [key]: "loading" }));
      apiClient
        .getOptionsPnlSnapshot({
          legs: (t.legs || []).map((l) => ({
            ...l,
            polygon_ticker: l.polygon_ticker,
            entry: l.entry,
            exit_price: l.exit_price ?? l.current_price,
          })),
          entry_at: t.fired_at,
          exit_at: t.closed_at,
          direction: t.direction,
          is_credit: !!t.is_credit,
          notional_usd: t.notional_usd,
        })
        .then((res) => {
          if (!cancelled) setSnapshots((prev) => ({ ...prev, [key]: res }));
        })
        .catch(() => {
          if (!cancelled) setSnapshots((prev) => ({ ...prev, [key]: "error" }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [trades, snapshots]);

  // Default order: active first (newest fired on top), then closed.
  const baseRows: TradeRow[] = useMemo(
    () => [
      ...activePositions
        .map(toRowFromActive)
        .sort((a, b) => (b.fired_at || "").localeCompare(a.fired_at || "")),
      ...trades.map((t) => {
        const snap = snapshots[snapshotKey(t)];
        return toRowFromClosed(
          t,
          snap && snap !== "loading" && snap !== "error" ? snap : undefined,
          snap === "loading",
        );
      }),
    ],
    [trades, activePositions, snapshots],
  );

  const rows = useMemo(() => {
    if (!sort) return baseRows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...baseRows].sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [baseRows, sort]);

  // Union of all criteria keys across rows, in first-seen order.
  const criteriaKeys: string[] = [];
  for (const t of baseRows) {
    if (!t.criteria) continue;
    for (const k of Object.keys(t.criteria)) {
      if (!criteriaKeys.includes(k)) criteriaKeys.push(k);
    }
  }

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-base font-bold text-white">All trades</h3>
        <span className="text-[11px] font-semibold text-white/70">
          {activePositions.length} active · {trades.length} closed. Click a column to sort, a row for the chart.
        </span>
      </div>
      <div ref={scrollRef} className="overflow-x-auto rounded-sm border border-white/10">
        {/* Header order MUST match the body in Row(): Trade, Status, PnL, Fired, Entry, Exit/Now, TP/SL, criteria */}
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-wider text-white/80 bg-black/60">
              <SortableTh label="Trade" colKey="trade" align="left" sort={sort} onSort={onSort} />
              <SortableTh label="Status" colKey="status" align="left" sort={sort} onSort={onSort} />
              <SortableTh label="PnL" colKey="pnl" sort={sort} onSort={onSort} />
              <SortableTh label="Fired" colKey="fired" sort={sort} onSort={onSort} />
              <SortableTh label="Entry" colKey="entry" sort={sort} onSort={onSort} />
              <SortableTh label="Exit / Now" colKey="mark" sort={sort} onSort={onSort} />
              <th className="text-right px-3 py-2 whitespace-nowrap">TP / SL</th>
              {criteriaKeys.map((k) => (
                <SortableTh key={k} label={k} colKey={k} sort={sort} onSort={onSort} />
              ))}
            </tr>
          </thead>
          <tbody className="bg-black/20">
            {rows.map((t) => (
              <Row key={t.key} trade={t} criteriaKeys={criteriaKeys} viewportW={viewportW} isMock={isMock} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
